import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Chip, CircularProgress,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import PeopleIcon from "@material-ui/icons/People";
import { useDispatch, useSelector } from "react-redux";
import { isSuper } from "../../lib/permissions";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import { engSegmentListAction, engSegmentCreateAction, engSegmentPreviewAction, engResetMutation } from "../../containers/engagement/action";
import EmployerSelect from "../employerSelect";
import { tokens, toMessages, SUCCESS_MESSAGES, ERROR_FALLBACKS } from "./utils";

const RULE_TYPES = [
  {
    value: "USER_SEGMENT",
    label: "Activation Stage",
    allowed_operators: ["EQ", "NE"]
  },
  {
    value: "EMPLOYER",
    label: "Employer IDs",
    allowed_operators: ["IN", "NOT_IN", "EQ", "NE"]
  },
  {
    value: "ACTIVITY_LEVEL",
    label: "Tx Count (30d)",
    allowed_operators: ["GT", "GTE", "LT", "LTE", "EQ", "NE", "IN", "NOT_IN"]
  },
  {
    value: "SALARY_THRESHOLD",
    label: "Salary Threshold",
    allowed_operators: ["GT", "GTE", "LT", "LTE", "EQ", "NE", "IN", "NOT_IN"]
  },
  {
    value: "LAST_TRANSACTION_DATE",
    label: "Days Since Last Tx",
    allowed_operators: ["GT", "GTE", "LT", "LTE", "EQ", "NE"]
  },
  {
    value: "USER_TAG",
    label: "User Tag",
    allowed_operators: ["IN", "NOT_IN"]
  },
  {
    value: "USER_EMAILS",
    label: "User Emails",
    allowed_operators: ["IN", "NOT_IN"]
  },
];

const OPERATORS = [
  { value: "EQ", label: "=" },
  { value: "NE", label: "≠" },
  { value: "GT", label: ">" },
  { value: "GTE", label: "≥" },
  { value: "LT", label: "<" },
  { value: "LTE", label: "≤" },
  { value: "IN", label: "In" },
  { value: "NOT_IN", label: "Not In" },
];

const emptyRule = () => ({ rule_type: "ACTIVITY_LEVEL", operator: "GTE", value_number: "1", value_text: "", value_json: "", logic: "AND", group: 0 });

const useStyles = makeStyles(() => ({
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  title: { fontSize: 20, fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue },
  createBtn: {
    backgroundColor: theme.colors.DodgerBlue,
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    "&:hover": { backgroundColor: theme.colors.DodgerBlue, opacity: 0.88 },
  },
  card: {
    background: tokens.surface,
    borderRadius: 8,
    border: `1px solid ${tokens.border}`,
    padding: "16px 20px",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  segName: { fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue, fontSize: 15 },
  segMeta: { fontSize: 12, color: tokens.textMuted, marginTop: 4, fontFamily: theme.fontFamily.MARKPRO },
  previewChip: { marginLeft: 12, cursor: "pointer" },
  previewBox: {
    background: tokens.tint,
    border: `1px solid ${theme.colors.Periwinkle}`,
    borderRadius: 6,
    padding: "10px 14px",
    marginTop: 10,
    fontSize: 13,
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPRO,
  },
  ruleRow: { display: "flex", gap: 8, alignItems: "center", marginBottom: 10 },
  addRuleBtn: { color: theme.colors.DodgerBlue, borderColor: theme.colors.DodgerBlue, fontFamily: theme.fontFamily.MARKPROMEDIUM },
  empty: { textAlign: "center", color: tokens.textSubtle, padding: "48px 0", fontFamily: theme.fontFamily.MARKPRO },
  dialogTitle: { color: theme.colors.DodgerBlue, fontFamily: theme.fontFamily.MARKPROBOLD },
  rulesLabel: { fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.DodgerBlue, marginBottom: 10 },
}));

export default function SegmentBuilder() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    segments, segmentPreview, loading,
    mutationLoading, mutationSuccess, mutationError, lastMutation,
  } = useSelector((s) => s.engagementReducer);

  // Creating segments is super-only; engagement.view users are read-only.
  const me = useSelector((s) => s.loginReducer?.me);
  let _storedUser = null;
  try {
    _storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    _storedUser = null;
  }
  const canManage = isSuper(me || _storedUser);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rules, setRules] = useState([emptyRule()]);
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    dispatch(engSegmentListAction({}));
  }, [dispatch]);

  // Success toast — only react to segment_create so we don't fire on campaign mutations
  // that share the same mutationSuccess flag.
  useEffect(() => {
    if (mutationSuccess && lastMutation === "segment_create") {
      enqueueSnackbar(SUCCESS_MESSAGES.segment_create, { variant: "success" });
      setOpen(false);
      setName(""); setDescription(""); setRules([emptyRule()]);
      dispatch(engResetMutation());
    }
  }, [mutationSuccess, lastMutation, dispatch, enqueueSnackbar]);

  useEffect(() => {
    if (mutationError && lastMutation === "segment_create") {
      toMessages(mutationError, ERROR_FALLBACKS.segment_create).forEach((m) =>
        enqueueSnackbar(m, { variant: "error" })
      );
      dispatch(engResetMutation());
    }
  }, [mutationError, lastMutation, dispatch, enqueueSnackbar]);

  const handlePreview = (id) => {
    setPreviewId(id);
    dispatch(engSegmentPreviewAction(id));
  };

  const handleRuleChange = (i, field, value) => {
    const next = rules.map((r, idx) => idx === i ? { ...r, [field]: value } : r);
    setRules(next);
  };

  const handleSubmit = () => {
    const payload = {
      name,
      description,
      rules: rules.map((r) => {
        const base = { rule_type: r.rule_type, operator: r.operator, logic: r.logic, group: r.group };
        if (["ACTIVITY_LEVEL", "SALARY_THRESHOLD", "LAST_TRANSACTION_DATE"].includes(r.rule_type))
          return { ...base, value_number: parseFloat(r.value_number) || 0 };
        if (["EMPLOYER", "USER_TAG", "USER_EMAILS"].includes(r.rule_type)) {
          // EMPLOYER comes from EmployerSelect as an array of UUIDs; the rest
          // are still comma-separated strings typed by the admin.
          if (Array.isArray(r.value_json)) {
            return { ...base, value_json: r.value_json };
          }
          return { ...base, value_json: r.value_json ? r.value_json.split(",").map((s) => s.trim()) : [] };
        }
        return { ...base, value_text: r.value_text };
      }),
    };
    dispatch(engSegmentCreateAction(payload));
  };

  return (
    <div>
      <div className={classes.header}>
        <span className={classes.title}>Segments</span>
        {canManage ? (
          <Button variant="contained" className={classes.createBtn} startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            New Segment
          </Button>
        ) : null}
      </div>

      {loading && segments.length === 0 && (
        <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
          <CircularProgress size={28} style={{ color: theme.colors.DodgerBlue }} />
        </div>
      )}

      {!loading && segments.length === 0 && (
        <div className={classes.empty}>No segments yet. Create one to start targeting users.</div>
      )}

      {segments.map((seg) => (
        <div key={seg.id} className={classes.card}>
          <div>
            <div className={classes.segName}>{seg.name}</div>
            <div className={classes.segMeta}>
              {seg.rules?.length || 0} rule{seg.rules?.length !== 1 ? "s" : ""}
              {seg.source_campaign ? ` · Campaign: ${seg.source_campaign}` : ""}
            </div>
            {previewId === seg.id && segmentPreview && (
              <div className={classes.previewBox}>
                <strong>{segmentPreview.count}</strong> users matched
                {segmentPreview.sample_users?.length > 0 && (
                  <div style={{ marginTop: 6, opacity: 0.8 }}>
                    Sample: {segmentPreview.sample_users.map((u) => `${u.first_name} ${u.last_name}`).join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Chip
              icon={<PeopleIcon style={{ fontSize: 14 }} />}
              label="Preview"
              size="small"
              className={classes.previewChip}
              onClick={() => handlePreview(seg.id)}
              style={{ borderColor: theme.colors.DodgerBlue, color: theme.colors.DodgerBlue }}
              variant="outlined"
            />
          </div>
        </div>
      ))}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle} style={{ fontSize: 18, padding: "16px 24px" }}>
          New Segment
        </DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} variant="outlined" size="small" style={{ marginBottom: 16 }} />
          <TextField label="Description" fullWidth value={description} onChange={(e) => setDescription(e.target.value)} variant="outlined" size="small" style={{ marginBottom: 20 }} />

          <div className={classes.rulesLabel}>Rules</div>
          {rules.map((rule, i) => (
            <div key={i} className={classes.ruleRow}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                <InputLabel>Type</InputLabel>
                <Select value={rule.rule_type} onChange={(e) => handleRuleChange(i, "rule_type", e.target.value)} label="Type">
                  {RULE_TYPES.map((rt) => <MenuItem key={rt.value} value={rt.value}>{rt.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 80 }}>
                <InputLabel>Op</InputLabel>
                <Select value={rule.operator} onChange={(e) => handleRuleChange(i, "operator", e.target.value)} label="Op">
                  {OPERATORS.filter((op) => RULE_TYPES?.find(
                    (rt) => rule?.rule_type === rt.value)?.allowed_operators?.includes(op.value)).map((op) => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                </Select>
              </FormControl>
              {["ACTIVITY_LEVEL", "SALARY_THRESHOLD", "LAST_TRANSACTION_DATE"].includes(rule.rule_type) ? (
                <TextField size="small" variant="outlined" label="Value" type="number" value={rule.value_number} onChange={(e) => handleRuleChange(i, "value_number", e.target.value)} style={{ width: 100 }} />
              ) : rule.rule_type === "EMPLOYER" ? (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <EmployerSelect
                    value={Array.isArray(rule.value_json)
                      ? rule.value_json
                      : (typeof rule.value_json === "string" && rule.value_json
                          ? rule.value_json.split(",").map((s) => s.trim()).filter(Boolean)
                          : [])}
                    onChange={(ids) => handleRuleChange(i, "value_json", ids)}
                    placeholder="Pick employers"
                  />
                </div>
              ) : ["USER_TAG", "USER_EMAILS"].includes(rule.rule_type) ? (
                <TextField size="small" variant="outlined" label="Comma-separated" value={rule.value_json} onChange={(e) => handleRuleChange(i, "value_json", e.target.value)} style={{ flex: 1 }} />
              ) : (
                <TextField size="small" variant="outlined" label="Value" value={rule.value_text} onChange={(e) => handleRuleChange(i, "value_text", e.target.value)} style={{ flex: 1 }} />
              )}
              <IconButton size="small" onClick={() => setRules(rules.filter((_, idx) => idx !== i))} disabled={rules.length === 1}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button variant="outlined" size="small" className={classes.addRuleBtn} startIcon={<AddIcon />} onClick={() => setRules([...rules, emptyRule()])}>
            Add Rule
          </Button>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setOpen(false)} style={{ color: tokens.textMuted }}>Cancel</Button>
          <Button variant="contained" className={classes.createBtn} onClick={handleSubmit} disabled={!name || mutationLoading}>
            {mutationLoading && lastMutation === "segment_create"
              ? <CircularProgress size={18} style={{ color: theme.colors.White }} />
              : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
